/**
 * Features Context
 *
 * Provides environment-level feature flags to the component tree.
 * Fetches from GET /api/features on mount and exposes boolean accessors.
 * On any fetch error, defaults silently to all-false — the app must never
 * fail to mount because the features endpoint is unreachable.
 */

import React, { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { getFeatures } from '../services/featuresService';
import type { FeaturesConfig } from '../services/featuresService';
import { FeaturesContext, type FeaturesContextType } from './FeaturesContext';

interface FeaturesProviderProps {
  children: ReactNode;
}

/**
 * Provides feature flag state to the component tree.
 * Place alongside other providers in App.tsx (does not depend on auth).
 */
export const FeaturesProvider: React.FC<FeaturesProviderProps> = ({ children }) => {
  const [features, setFeatures] = useState<FeaturesConfig>({
    flowerCatalog: false,
    flowerCatalogHasProducts: false,
    mailedCardCatalog: false,
    mailedCardCatalogHasArtworks: false,
    agentOrdersEnabled: false,
    publicAiPage: import.meta.env.VITE_PUBLIC_AI_PAGE === 'true',
    giftCatalog: false,
  });
  const [featuresResolved, setFeaturesResolved] = useState(false);

  const fetchFeatures = async () => {
    const config = await getFeatures();
    setFeatures(config);
    setFeaturesResolved(true);
  };

  useEffect(() => {
    fetchFeatures();
  }, []);

  const value: FeaturesContextType = {
    isFlowerCatalogEnabled: features.flowerCatalog,
    hasFlowerProducts: features.flowerCatalogHasProducts,
    isMailedCardCatalogEnabled: features.mailedCardCatalog,
    hasMailedCardArtworks: features.mailedCardCatalogHasArtworks,
    isAgentOrdersEnabled: features.agentOrdersEnabled,
    isPublicAiPageEnabled: features.publicAiPage,
    isGiftCatalogEnabled: features.giftCatalog,
    featuresResolved,
    refreshFeatures: fetchFeatures,
  };

  return (
    <FeaturesContext.Provider value={value}>
      {children}
    </FeaturesContext.Provider>
  );
};
